package com.dotdrives.csv

import com.dotdrives.model.NewGiver

data class CsvTable(
    val headers: List<String>,
    val rows: List<CsvRow>,
)

data class CsvRow(
    val rowNumber: Int,
    val values: List<String>,
)

data class CsvExistingGiver(
    val id: String,
    val displayName: String,
    val email: String?,
)

enum class GiverField {
    FirstName,
    LastName,
    DisplayName,
    SpouseName,
    Email,
    Phone,
    Address,
    Tags,
}

sealed class CsvRowResult {
    data class Ready(
        val giver: NewGiver,
    ) : CsvRowResult()

    data class MissingName(
        val row: CsvRow,
    ) : CsvRowResult()

    data class Duplicate(
        val giver: NewGiver,
        val existingId: String,
        val reason: String,
        val rowNumber: Int = 0,
        val existingDisplayName: String = "",
    ) : CsvRowResult()
}

object GiverCsv {
    fun parse(text: String): CsvTable {
        val records = parseRecords(text)
        if (records.isEmpty()) return CsvTable(emptyList(), emptyList())

        val headers = records.first().mapIndexed { index, value ->
            if (index == 0) value.removePrefix("\uFEFF").trim() else value.trim()
        }
        val rows = records
            .drop(1)
            .mapIndexedNotNull { index, values ->
                values
                    .takeIf { row -> row.any(String::isNotBlank) }
                    ?.let { CsvRow(rowNumber = index + 2, values = it) }
            }
        return CsvTable(headers = headers, rows = rows)
    }

    fun autoMap(headers: List<String>): Map<String, GiverField?> =
        headers.associateWith { header ->
            when (normalizeHeader(header)) {
                "firstname", "givenname" -> GiverField.FirstName
                "lastname", "surname", "familyname" -> GiverField.LastName
                "name", "displayname", "fullname", "givername", "donorname",
                "organization", "organizationname", "company", "companyname",
                -> GiverField.DisplayName
                "spouse", "spousename", "partner", "partnername" -> GiverField.SpouseName
                "email", "emailaddress" -> GiverField.Email
                "phone", "phonenumber", "telephone", "mobile", "cell" -> GiverField.Phone
                "address", "mailingaddress", "streetaddress" -> GiverField.Address
                "tag", "tags", "label", "labels" -> GiverField.Tags
                else -> null
            }
        }

    fun toGivers(
        table: CsvTable,
        mapping: Map<String, GiverField?>,
    ): List<CsvRowResult> = toGivers(table, mapping, emptyList())

    fun toGivers(
        table: CsvTable,
        mapping: Map<String, GiverField?>,
        existing: List<CsvExistingGiver>,
    ): List<CsvRowResult> =
        table.rows.map { row ->
            val fields = mappedFields(table.headers, row.values, mapping)
            val firstName = fields[GiverField.FirstName].orEmpty()
            val lastName = fields[GiverField.LastName].orEmpty()
            val explicitDisplayName = fields[GiverField.DisplayName].orEmpty()
            val displayName = explicitDisplayName.ifBlank {
                listOf(firstName, lastName).filter(String::isNotBlank).joinToString(" ")
            }

            if (displayName.isBlank()) {
                CsvRowResult.MissingName(row)
            } else {
                val giver = NewGiver(
                    giverType = "individual",
                    firstName = firstName,
                    lastName = lastName,
                    displayName = displayName,
                    spouseName = fields[GiverField.SpouseName].nullIfBlank(),
                    email = fields[GiverField.Email].nullIfBlank(),
                    phone = fields[GiverField.Phone].nullIfBlank(),
                    address = fields[GiverField.Address].nullIfBlank(),
                    stewardUserId = null,
                    tags = parseTags(fields[GiverField.Tags]),
                )
                duplicateFor(giver, row.rowNumber, existing) ?: CsvRowResult.Ready(giver)
            }
        }

    private fun mappedFields(
        headers: List<String>,
        values: List<String>,
        mapping: Map<String, GiverField?>,
    ): Map<GiverField, String> {
        val fields = mutableMapOf<GiverField, String>()
        headers.forEachIndexed { index, header ->
            val field = mapping[header] ?: return@forEachIndexed
            val value = values.getOrElse(index) { "" }.trim()
            if (value.isNotEmpty() && fields[field].isNullOrEmpty()) {
                fields[field] = value
            }
        }
        return fields
    }

    private fun duplicateFor(
        giver: NewGiver,
        rowNumber: Int,
        existing: List<CsvExistingGiver>,
    ): CsvRowResult.Duplicate? {
        val normalizedEmail = giver.email.normalized()
        val emailMatch = normalizedEmail?.let { email ->
            existing.firstOrNull { it.email.normalized() == email }
        }
        if (emailMatch != null) {
            return CsvRowResult.Duplicate(
                giver = giver,
                existingId = emailMatch.id,
                reason = "same email",
                rowNumber = rowNumber,
                existingDisplayName = emailMatch.displayName,
            )
        }

        val normalizedName = giver.displayName.normalized()
        val nameMatch = existing.firstOrNull { it.displayName.normalized() == normalizedName }
        return nameMatch?.let {
            CsvRowResult.Duplicate(
                giver = giver,
                existingId = it.id,
                reason = "same name",
                rowNumber = rowNumber,
                existingDisplayName = it.displayName,
            )
        }
    }
}

private fun parseRecords(text: String): List<List<String>> {
    if (text.isEmpty()) return emptyList()

    val records = mutableListOf<List<String>>()
    val record = mutableListOf<String>()
    val field = StringBuilder()
    var quoted = false
    var index = 0

    fun finishField() {
        record += field.toString()
        field.clear()
    }

    fun finishRecord() {
        finishField()
        records += record.toList()
        record.clear()
    }

    while (index < text.length) {
        val character = text[index]
        when {
            character == '"' && quoted && text.getOrNull(index + 1) == '"' -> {
                field.append('"')
                index += 1
            }
            character == '"' -> quoted = !quoted
            character == ',' && !quoted -> finishField()
            (character == '\r' || character == '\n') && !quoted -> {
                finishRecord()
                if (character == '\r' && text.getOrNull(index + 1) == '\n') index += 1
            }
            else -> field.append(character)
        }
        index += 1
    }

    if (field.isNotEmpty() || record.isNotEmpty()) finishRecord()
    return records
}

private fun normalizeHeader(value: String): String =
    value.lowercase().filter(Char::isLetterOrDigit)

private fun String?.nullIfBlank(): String? =
    this?.trim()?.takeIf(String::isNotEmpty)

private fun String?.normalized(): String? =
    nullIfBlank()?.lowercase()

private fun parseTags(value: String?): List<String> =
    value
        ?.split(',', ';', '|')
        ?.map(String::trim)
        ?.filter(String::isNotEmpty)
        ?.distinct()
        .orEmpty()
